import useModal from '@/shared/hooks/useModal';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useLoadingOverlayStore } from '@/shared/store/loadingOverlayStore';
import { getApiErrorMessage, isCanceledError } from '@/shared/utils/queryUtils';

type UseExecMutationFetchParams<TVariables, TResponse> = {
  mutationFn: (variables: TVariables) => Promise<TResponse>;
  successHandler?: (data: TResponse, variables: TVariables) => void;
  failHandler?: (error: any) => void;
  retryMessage?: string;
  invalidateKeys?: QueryKey[];
  withLoadingOverlay?: boolean;
};

export const useExecMutationFetch = <TVariables = any, TResponse = any>({
  mutationFn,
  successHandler,
  failHandler,
  retryMessage,
  invalidateKeys = [],
  withLoadingOverlay = true,
}: UseExecMutationFetchParams<TVariables, TResponse>) => {
  const setIsLoading = useLoadingOverlayStore((state) => state.setIsLoading);
  const modal = useModal();

  const queryClient = useQueryClient();
  const lastVariablesRef = useRef<TVariables | null>(null);
  const isRetryInProgress = useRef(false);

  const mutation = useMutation({
    mutationFn,
    onMutate: (variables) => {
      lastVariablesRef.current = variables;
      if (withLoadingOverlay) setIsLoading(true);
    },

    onSuccess: async (data) => {
      if (withLoadingOverlay) setIsLoading(false);
      isRetryInProgress.current = false;
      successHandler?.(data, lastVariablesRef.current as TVariables);
      for (const key of invalidateKeys) {
        await queryClient.invalidateQueries({
          queryKey: key,
          refetchType: 'active',
        });
      }
    },

    onError: async (error: any) => {
      if (withLoadingOverlay) setIsLoading(false);
      if (isCanceledError(error)) return;

      console.error(`\n┏━━━ ⚠️ ⚠️ error ⚠️ ⚠️ ━━━\n`, error, `\n┗━━━━━━ ⚠️ ⚠️ ⚠️ ⚠️ ⚠️ ━━━━━━━━━\n`);
      isRetryInProgress.current = false;
      if (failHandler) {
        failHandler(error);
      } else if (retryMessage) {
        const apiMessage = getApiErrorMessage(error);
        const fullMessage = apiMessage ? `${retryMessage}\n\n${apiMessage}` : retryMessage;
        const confirmed = await modal.confirm(fullMessage);
        if (confirmed) retry();
      } else {
        const apiMessage = getApiErrorMessage(error);
        await modal.alert(apiMessage ?? '요청 처리 중 오류가 발생했습니다.\n문제가 지속되면 관리자에게 문의해 주세요.');
      }
    },
  });

  const retry = () => {
    if (isRetryInProgress.current) return;
    if (lastVariablesRef.current) {
      isRetryInProgress.current = true;
      if (mutation.isError) mutation.reset();
      setTimeout(() => {
        mutation.mutate(lastVariablesRef.current as TVariables);
      }, 10);
    }
  };

  useEffect(() => {
    return () => {
      isRetryInProgress.current = false;
    };
  }, []);

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    retry,
  };
};
